import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Award, TrendingUp, Database } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AccuracyScorecard — a collapsed-by-default expandable card that shows
 * the learned per-model accuracy for a given location. Pulls from
 * /api/forecast/accuracy which hits the Mongo `model_stats` collection
 * populated by the daily learn_from_actuals scheduler job.
 *
 * Props:
 *   lat, lon — coordinates of the location to report on
 *   label    — human label shown in the header ("Brooklyn, NY")
 *
 * UI contract:
 *   - Closed state shows just the summary line ("6 models tracked · best: ECMWF ±1.2°F")
 *   - Open state shows one row per model with MAE + weight multiplier + sample count
 *   - Cold-start message when sample_count < 3 for all models
 *
 * Why this exists: lets users (and the App Store reviewer) see the
 * ensemble actually learning their microclimate. Doubles as a trust
 * signal — "this app doesn't just average 6 models, it adapts to YOU".
 */
export default function AccuracyScorecard({ lat, lon, label }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (lat == null || lon == null) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/forecast/accuracy`, {
          params: { lat, lon },
        });
        if (!cancelled) setRows(r.data?.sources || []);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load accuracy");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lat, lon]);

  if (err) return null;
  if (rows == null) return null;

  const best = rows.length > 0 ? rows[0] : null;
  const hasEnoughData = rows.some((r) => (r.sample_count || 0) >= 3);

  return (
    <div
      className="mt-6 border-2 border-black bg-white max-w-[640px] mx-auto"
      data-testid="accuracy-scorecard"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#FFFBE6] transition-colors"
        data-testid="accuracy-scorecard-toggle"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Award className="w-5 h-5 shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-sm font-black tracking-tight leading-tight">
              Model Accuracy · Last 14 days
            </div>
            <div className="text-xs text-neutral-600 leading-tight mt-0.5 truncate">
              {label ? <>at {label} · </> : null}
              {hasEnoughData && best ? (
                <>
                  {rows.length} models tracked · best:{" "}
                  <span className="font-semibold">{best.display}</span> ±
                  {best.combined_mae}°F
                </>
              ) : (
                <>Learning this location — check back in a few days</>
              )}
            </div>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="px-4 pb-4 pt-2 border-t-2 border-black"
          data-testid="accuracy-scorecard-body"
        >
          {!hasEnoughData && (
            <p className="text-xs text-neutral-600 py-4">
              Weathervane is still learning the microclimate at this spot.
              Once we have at least 3 days of observed data per model (usually
              within a week), this card starts showing each model's mean
              absolute error (MAE) and the weight multiplier we apply when
              blending the ensemble.
            </p>
          )}
          {hasEnoughData && (
            <>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-600 mb-2">
                Per-model error (lower = better)
              </div>
              <ul className="divide-y divide-neutral-200">
                {rows.map((r, i) => {
                  const enough = (r.sample_count || 0) >= 3;
                  const pct = r.weight_multiplier
                    ? Math.round(r.weight_multiplier * 100)
                    : null;
                  return (
                    <li
                      key={r.source}
                      className="flex items-center gap-3 py-2"
                      data-testid={`accuracy-row-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {r.display}
                        </div>
                        <div className="text-[11px] text-neutral-500">
                          {enough ? (
                            <>
                              {r.sample_count} samples
                              {r.high_bias != null && (
                                <>
                                  {" · bias "}
                                  {r.high_bias > 0 ? "+" : ""}
                                  {r.high_bias.toFixed(1)}°F high
                                </>
                              )}
                            </>
                          ) : (
                            <>{r.sample_count} samples — warming up</>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-display font-black text-sm tabular-nums leading-tight">
                          {enough ? `±${r.combined_mae}°F` : "—"}
                        </div>
                        {enough && pct != null && (
                          <div
                            className={`text-[10px] uppercase tracking-wider font-bold ${
                              pct >= 90
                                ? "text-green-700"
                                : pct >= 60
                                ? "text-neutral-600"
                                : "text-red-700"
                            }`}
                          >
                            {pct}% weight
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <div className="flex items-start gap-2 mt-3 pt-3 border-t border-neutral-200 text-[11px] text-neutral-600 leading-snug">
                <TrendingUp className="w-3 h-3 shrink-0 mt-0.5" />
                <div>
                  The worst-performing model gets down-weighted (floor 40%) so
                  chronically-wrong models lose influence here but never go
                  silent. Signed bias is subtracted before averaging so
                  systematically hot/cold models get auto-corrected.
                </div>
              </div>
              <div className="flex items-start gap-2 mt-2 text-[11px] text-neutral-600 leading-snug">
                <Database className="w-3 h-3 shrink-0 mt-0.5" />
                <div>
                  Observed actuals pulled daily at 02:00 UTC from Open-Meteo's
                  archive API; rolling window = last 14 days.
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
